// btw-front/src/components/ui/CompanySearchSelect.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import { search as searchCompanies, customerTaken } from "../../api/companySearch";
import {
  formatCustomerBusinessNumber,
  businessNumberFromCompanyMeta,
  splitBusinessNumber,
  joinBusinessNumber,
  remainingDigits,
  isBusinessNumberSuspicious,
  PUBLIC_PREFIX_LENGTH,
} from "../../lib/customerBusinessNumber";

/**
 * 고객사명 검색·선택 콤보박스 (자유입력 차단 — 목록에서 "선택"할 때만 값이 들어감).
 *  props
 *   - value: 선택된 상호(string)
 *   - onChange(name, meta): 선택/해제 시 호출. meta = 선택 레코드(해제 시 null)
 *   - excludeDealId: 수정 중 자기 딜 제외용(선택)
 *   - alertOnTaken: 중복(선점) 고객사 선택 시 안내 모달까지 띄움. 파트너 폼 전용
 *     (관리자=총판 본인이라 "총판 문의" 문구가 부적합 → 관리자 폼은 인라인 메시지만).
 *   - businessNumber / onBusinessNumberChange: 고객사 사업자등록번호. onBusinessNumberChange 를 주면
 *     회사명 아래에 번호 줄이 함께 렌더된다. 파트너는 고객사 번호를 모르므로 직접 입력받지
 *     않고, 고객사 선택 시 **공공데이터가 준 앞자리를 읽기 전용으로 채운다**. 뒷자리는 아는
 *     경우에만 선택 입력. 해제 시 함께 비운다.
 *     이 값은 등록을 막지 않는다 — 차단은 고객사 이름으로만 하고 번호는 중복 확인 보조 신호다.
 *   - label, required, disabled, error, className
 */
// 유사 고객사 안내에 이름을 몇 건까지 펼쳐 보일지 (나머지는 "외 N건")
const SIMILAR_PREVIEW = 3;

export function CompanySearchSelect({
  value = "",
  onChange,
  excludeDealId,
  alertOnTaken = false,
  businessNumber = "",
  onBusinessNumberChange,
  label,
  required = false,
  disabled = false,
  error,
  className = "",
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false); // 중복 체크 중
  const [taken, setTaken] = useState(false); // 다른 파트너가 이미 등록 → 선택 차단
  const [similar, setSimilar] = useState([]); // 유사 고객사 — 차단하지 않고 안내만
  const boxRef = useRef(null);
  const reqIdRef = useRef(0);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // 300ms 디바운스 검색 (2글자 이상)
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setOpen(true); // 검색 시작 즉시 드롭다운 오픈 — '검색 중…' 인디케이터가 보이도록
    const myReq = ++reqIdRef.current;
    const timer = setTimeout(() => {
      searchCompanies(q)
        .then((rows) => {
          if (myReq !== reqIdRef.current) return;
          setItems(rows);
          setOpen(true);
        })
        .catch(() => {
          if (myReq === reqIdRef.current) setItems([]);
        })
        .finally(() => {
          if (myReq === reqIdRef.current) setLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  async function pick(item) {
    setOpen(false);
    setQuery("");
    setItems([]);
    setTaken(false);
    setSimilar([]);
    setChecking(true);
    // 조회 결과의 앞자리를 그대로 채운다(실측상 6자리). 차단에 쓰지 않으므로 자릿수·체크섬을 가리지 않는다
    const picked = businessNumberFromCompanyMeta(item);
    try {
      const { taken: isTaken, similar: rows } = await customerTaken(
        item.name,
        excludeDealId,
        picked,
      );
      if (rows && rows.length) setSimilar(rows);
      if (isTaken) {
        // 다른 파트너(관리자)가 이미 등록 — 선택 차단(값 미커밋)
        setTaken(true);
        setSimilar([]);
        if (onChange) onChange("", null);
        if (onBusinessNumberChange) onBusinessNumberChange("");
        // 파트너 폼: 인라인 메시지에 더해 안내 모달로 명확히 차단 사유 전달
        if (alertOnTaken) {
          Modal.warning({
            title: t("partners.deals.form.customerCompanyTakenAlertTitle"),
            content: t("partners.deals.form.customerCompanyTakenAlertBody"),
            okText: t("partners.deals.form.customerCompanyTakenAlertOk"),
          });
        }
        return;
      }
      if (onChange) onChange(item.name, item);
      if (onBusinessNumberChange) onBusinessNumberChange(picked);
    } catch (e) {
      // 체크 실패 시 보수적으로 통과 — 서버가 최종 차단(409)
      if (onChange) onChange(item.name, item);
      if (onBusinessNumberChange) onBusinessNumberChange(picked);
    } finally {
      setChecking(false);
    }
  }

  function clearSelection() {
    if (onChange) onChange("", null);
    if (onBusinessNumberChange) onBusinessNumberChange("");
    setQuery("");
    setItems([]);
    setSimilar([]);
    setOpen(false);
  }

  const hasSelection = !!value;
  // 앞 6자리는 늘 조회분 — 수정 화면에서 기존 딜을 열어도 같은 기준으로 나뉜다
  const { prefix, suffix } = splitBusinessNumber(businessNumber, PUBLIC_PREFIX_LENGTH);
  const suffixMax = remainingDigits(prefix);
  // 10자리를 채웠는데 체크섬이 어긋나면 확인만 권한다(등록은 막지 않는다)
  const businessNumberSuspicious = isBusinessNumberSuspicious(businessNumber);

  return (
    <div className={`flex flex-col gap-1.5 ${className}`} ref={boxRef}>
      {label && (
        <label className="text-xs font-medium text-slate-700">
          {label}
          {required && <span className="ml-0.5 text-rose-500">*</span>}
        </label>
      )}

      {disabled ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          {value || "-"}
        </div>
      ) : hasSelection ? (
        <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
            {value}
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="shrink-0 text-xs font-medium text-accent underline-offset-2 hover:underline"
          >
            {t("partners.deals.form.customerCompanyChange")}
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={query}
            maxLength={50}
            onChange={(e) => {
              setQuery(e.target.value);
              if (taken) setTaken(false);
            }}
            onFocus={() => {
              if (items.length) setOpen(true);
            }}
            placeholder={t("partners.deals.form.customerCompanySearchPlaceholder")}
            className={`w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-accent/30 ${
              error || taken
                ? "border-rose-400 focus:border-rose-500"
                : query.trim()
                  ? "border-amber-400 focus:border-amber-500"
                  : "border-slate-300 focus:border-accent"
            }`}
            aria-invalid={!!error}
            autoComplete="off"
          />
          {open && (
            <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {loading ? (
                <p className="px-3 py-3 text-xs text-slate-400">
                  {t("partners.deals.form.customerCompanySearching")}
                </p>
              ) : items.length === 0 ? (
                <p className="px-3 py-3 text-xs text-slate-400">
                  {t("partners.deals.form.customerCompanyNoResults")}
                </p>
              ) : (
                items.map((it, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => pick(it)}
                    className="flex w-full flex-col gap-0.5 border-b border-slate-100 px-3 py-2 text-left transition last:border-b-0 hover:bg-slate-50"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{it.name}</span>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                        {it.isCorp
                          ? t("partners.deals.form.customerCompanyCorp")
                          : t("partners.deals.form.customerCompanyIndividual")}
                      </span>
                    </span>
                    {it.businessNumberPrefix && (
                      <span className="text-xs text-slate-500">
                        {t("partners.deals.form.customerBusinessNumber")}{" "}
                        {formatCustomerBusinessNumber(it.businessNumberPrefix) ||
                          it.businessNumberPrefix}
                      </span>
                    )}
                    {(it.address || it.industry) && (
                      <span className="truncate text-xs text-slate-500">
                        {it.address}
                        {it.address && it.industry ? " · " : ""}
                        {it.industry}
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {error ? (
        <p className="text-xs text-rose-600">{error}</p>
      ) : taken ? (
        <p className="text-xs font-medium text-rose-600">
          {t("partners.deals.form.customerCompanyTaken")}
        </p>
      ) : checking ? (
        <p className="text-xs text-slate-500">{t("partners.deals.form.customerCompanyChecking")}</p>
      ) : disabled || hasSelection ? null : query.trim() ? (
        <p className="text-xs font-medium text-amber-600">
          {t("partners.deals.form.customerCompanyMustSelect")}
        </p>
      ) : (
        <p className="text-xs text-slate-500">{t("partners.deals.form.customerCompanyHint")}</p>
      )}

      {/* 유사 고객사 — 표기·앞자리만 비슷한 건이라 차단하지 않고 확인만 요청 */}
      {!taken && similar.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs font-medium text-amber-700">
            {t("partners.deals.form.similarCustomer", { count: similar.length })}
          </p>
          <p className="mt-0.5 text-xs text-amber-700">
            {/* 이름은 최대 3건만 — 그 이상은 한 줄이 길어져 읽히지 않는다 */}
            {similar
              .slice(0, SIMILAR_PREVIEW)
              .map((x) => x.customerCompany)
              .join(", ")}
            {similar.length > SIMILAR_PREVIEW &&
              ` ${t("partners.deals.form.similarCustomerMore", {
                count: similar.length - SIMILAR_PREVIEW,
              })}`}
          </p>
          <p className="mt-0.5 text-xs text-amber-600">
            {t("partners.deals.form.similarCustomerHint")}
          </p>
        </div>
      )}

      {/* 고객사 사업자등록번호 — 앞자리는 공공데이터분(읽기 전용), 뒷자리만 선택 입력.
          회사를 고르기 전에는 띄우지 않는다.
          고른 뒤에는 **번호가 없어도 줄을 유지**한다 — 대부분의 사업장 레코드에는 번호가 있지만
          드물게 비어 있고, 그때 줄이 통째로 사라지면 쓰는 사람은 버그로 읽는다(실제 문의 발생).
          없을 때는 그 사실과 "없어도 등록된다"는 것을 한 줄로 알린다. */}
      {onBusinessNumberChange && hasSelection && (
        <div className="mt-1 flex flex-col gap-1.5">
          <label className="text-xs font-medium text-slate-700">
            {t("partners.deals.form.customerBusinessNumber")}
          </label>
          {!prefix ? (
            <p className="text-xs text-slate-500">
              {t("partners.deals.form.customerBusinessNumberNone")}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {/* 앞자리 — 조회 결과 그대로. 파트너가 바꿀 값이 아니다.
                하이픈으로 끊지 않는다 — 6자리를 3-2-5 로 끊으면 마지막 묶음이 시작된 것처럼 보인다. */}
                <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm tabular-nums text-slate-700">
                  {prefix}
                </span>
                {suffixMax > 0 &&
                  (disabled ? (
                    <span className="text-sm tabular-nums text-slate-600">{suffix || "-"}</span>
                  ) : (
                    <>
                      <span className="text-slate-300">-</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={suffix}
                        onChange={(e) =>
                          onBusinessNumberChange(joinBusinessNumber(prefix, e.target.value))
                        }
                        placeholder={"0".repeat(suffixMax)}
                        maxLength={suffixMax}
                        className={`w-24 rounded-lg border bg-white px-3 py-2 text-sm tabular-nums text-slate-900 outline-none transition placeholder:text-slate-300 focus:ring-2 focus:ring-accent/30 ${
                          businessNumberSuspicious
                            ? "border-amber-400 focus:border-amber-500"
                            : "border-slate-300 focus:border-accent"
                        }`}
                        autoComplete="off"
                      />
                    </>
                  ))}
              </div>
              {businessNumberSuspicious ? (
                <p className="text-xs font-medium text-amber-600">
                  {t("partners.deals.form.customerBusinessNumberSuspicious")}
                </p>
              ) : (
                !disabled &&
                suffixMax > 0 && (
                  <p className="text-xs text-slate-500">
                    {t("partners.deals.form.customerBusinessNumberHint")}
                  </p>
                )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
